import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Timeline,
  Loader,
  Text,
  Box,
  Group,
  Badge,
  Collapse,
  Button,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconAlertCircle,
  IconClock,
  IconCheck,
  IconX,
  IconLoader,
} from '@tabler/icons';
import { useAdminIntegrationTasks } from '../../hooks/use-admin-integration-tasks';
import type { AdminIntegrationTask } from '../../api/admin-integration-tasks';

interface IntegrationSyncRunsTimelineProps {
  accountId: string;
  integrationKey: string;
}

const PAGE_SIZE = 20;

type StatusMeta = { color: string; icon: React.ReactNode };

const DEFAULT_STATUS_META: StatusMeta = { color: 'blue', icon: <IconClock size={16} /> };

const STATUS_META: Record<string, StatusMeta> = {
  completed: { color: 'green', icon: <IconCheck size={16} /> },
  failed: { color: 'red', icon: <IconX size={16} /> },
  in_progress: { color: 'blue', icon: <IconLoader size={16} /> },
  pending: { color: 'gray', icon: <IconClock size={16} /> },
};

export const IntegrationSyncRunsTimeline: React.FC<IntegrationSyncRunsTimelineProps> = ({
  accountId,
  integrationKey,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tab, setTab] = useState<'active' | 'completed' | 'failed'>('active');
  const scrollRef = useRef<HTMLDivElement>(null);

  const status =
    tab === 'active' ? 'pending,in_progress' : tab === 'completed' ? 'completed' : 'failed';

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminIntegrationTasks(accountId, integrationKey, PAGE_SIZE, status);

  const tasks: AdminIntegrationTask[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isLoading || isFetchingNextPage || !hasNextPage) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      fetchNextPage();
    }
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [tab, accountId, integrationKey]);

  // Render a single task
  const renderTask = (task: AdminIntegrationTask) => {
    const isOpen = expanded === task._id;
    const taskStatus = task.execution?.status ?? 'unknown';
    const { color: statusColor, icon: statusIcon } = STATUS_META[taskStatus] ?? DEFAULT_STATUS_META;

    return (
      <Timeline.Item
        key={task._id}
        title={
          <Group position="apart" noWrap>
            <Group spacing="xs">
              <Text fw={500}>{task.objectName ?? '—'}</Text>
              {task.type && (
                <Badge size="sm" color="indigo">
                  {task.type}
                </Badge>
              )}
            </Group>
            <Badge color={statusColor} leftSection={statusIcon} size="sm">
              {taskStatus.replace('_', ' ')}
            </Badge>
          </Group>
        }
        bullet={
          <Button
            variant="subtle"
            compact
            onClick={() => setExpanded(isOpen ? null : task._id)}
            style={{ padding: 0, minWidth: 24, height: 24 }}
          >
            {isOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Button>
        }
        lineVariant={task.execution?.status === 'failed' ? 'dashed' : 'solid'}
      >
        <Group spacing={8} mb={4}>
          <Text size="xs" color="dimmed">
            Date Range:
          </Text>
          <Text size="xs">
            {task.options?.startDate ? new Date(task.options.startDate).toLocaleDateString() : '-'}
            {' - '}
            {task.options?.endDate ? new Date(task.options.endDate).toLocaleDateString() : '-'}
          </Text>
          {task.execution?.stats && (
            <>
              <Text size="xs" color="dimmed">
                •
              </Text>
              <Text size="xs" color="dimmed">
                Processed:
              </Text>
              <Text size="xs" fw={500}>
                {task.execution.stats.processedCount || 0}
              </Text>
            </>
          )}
        </Group>

        <Collapse in={isOpen} transitionDuration={200}>
          <Paper withBorder p="sm" mt="xs">
            <Stack spacing="xs">
              <Group spacing="lg">
                <Text fw={500} size="sm">
                  Task Details
                </Text>
              </Group>

              <Group spacing="md">
                <Text size="xs" color="dimmed">
                  Object:
                </Text>
                <Text size="xs">{task.objectName ?? '—'}</Text>
                {task.type && (
                  <>
                    <Text size="xs" color="dimmed">
                      Type:
                    </Text>
                    <Text size="xs">{task.type}</Text>
                  </>
                )}
                <Text size="xs" color="dimmed">
                  Origin:
                </Text>
                <Text size="xs">{task.origin}</Text>
              </Group>

              {task.options && (
                <Box>
                  <Text fw={500} size="sm" mb="xs">
                    Sync Options
                  </Text>
                  <Group spacing="md">
                    {task.options.startDate && (
                      <>
                        <Text size="xs" color="dimmed">
                          Data From:
                        </Text>
                        <Text size="xs">
                          {new Date(task.options.startDate).toLocaleDateString()}
                        </Text>
                      </>
                    )}
                    {task.options.endDate && (
                      <>
                        <Text size="xs" color="dimmed">
                          Data To:
                        </Text>
                        <Text size="xs">{new Date(task.options.endDate).toLocaleDateString()}</Text>
                      </>
                    )}
                    {task.options.dateField && (
                      <>
                        <Text size="xs" color="dimmed">
                          Date Field:
                        </Text>
                        <Text size="xs">{task.options.dateField}</Text>
                      </>
                    )}
                  </Group>
                </Box>
              )}

              <Box>
                <Text fw={500} size="sm" mb="xs">
                  Execution Details
                </Text>
                <Group spacing="md">
                  <Text size="xs" color="dimmed">
                    Started:
                  </Text>
                  <Text size="xs">
                    {task.execution?.startDate
                      ? new Date(task.execution.startDate).toLocaleString()
                      : '-'}
                  </Text>
                  {task.execution?.finishDate && (
                    <>
                      <Text size="xs" color="dimmed">
                        Finished:
                      </Text>
                      <Text size="xs">{new Date(task.execution.finishDate).toLocaleString()}</Text>
                    </>
                  )}
                </Group>
              </Box>

              {task.execution?.stats && (
                <Box>
                  <Text fw={500} size="sm" mb="xs">
                    Statistics
                  </Text>
                  <Group spacing="md">
                    {task.execution.stats.apiCallCount !== undefined && (
                      <>
                        <Text size="xs" color="dimmed">
                          API Calls:
                        </Text>
                        <Text size="xs" fw={500}>
                          {task.execution.stats.apiCallCount}
                        </Text>
                      </>
                    )}
                    {task.execution.stats.receivedCount !== undefined && (
                      <>
                        <Text size="xs" color="dimmed">
                          Received:
                        </Text>
                        <Text size="xs" fw={500}>
                          {task.execution.stats.receivedCount}
                        </Text>
                      </>
                    )}
                    {task.execution.stats.processedCount !== undefined && (
                      <>
                        <Text size="xs" color="dimmed">
                          Processed:
                        </Text>
                        <Text size="xs" fw={500}>
                          {task.execution.stats.processedCount}
                        </Text>
                      </>
                    )}
                  </Group>
                </Box>
              )}

              {task.lastModified && (
                <Group spacing="md">
                  <Text size="xs" color="dimmed">
                    Last Modified:
                  </Text>
                  <Text size="xs">{new Date(task.lastModified).toLocaleString()}</Text>
                </Group>
              )}
            </Stack>
          </Paper>
        </Collapse>
      </Timeline.Item>
    );
  };

  const getTabCount = () => {
    if (!data) return '';
    return total > 0 ? ` (${total})` : '';
  };

  return (
    <Box>
      <SegmentedControl
        value={tab}
        onChange={(value: 'active' | 'completed' | 'failed') => setTab(value)}
        data={[
          { label: `Active${tab === 'active' ? getTabCount() : ''}`, value: 'active' },
          { label: `Completed${tab === 'completed' ? getTabCount() : ''}`, value: 'completed' },
          { label: `Failed${tab === 'failed' ? getTabCount() : ''}`, value: 'failed' },
        ]}
        fullWidth
        mb="md"
      />

      <ScrollArea style={{ height: 500 }} viewportRef={scrollRef}>
        <Box p="md">
          {isError && (
            <Group position="center" mb="md">
              <IconAlertCircle color="red" size={20} />
              <Text color="red">Failed to load tasks. {error?.message}</Text>
            </Group>
          )}

          {!isLoading && tasks.length === 0 && (
            <Text align="center" color="dimmed">
              No {tab} tasks found for this integration.
            </Text>
          )}

          {tasks.length > 0 && (
            <Timeline active={0} bulletSize={28} lineWidth={2}>
              {tasks.map(renderTask)}

              {(isLoading || isFetchingNextPage) && (
                <Timeline.Item>
                  <Group position="center">
                    <Loader size="sm" />
                    <Text size="xs" color="dimmed">
                      {isLoading ? 'Loading tasks...' : 'Loading more tasks...'}
                    </Text>
                  </Group>
                </Timeline.Item>
              )}

              {!hasNextPage && !isLoading && !isFetchingNextPage && tasks.length > 0 && (
                <Timeline.Item>
                  <Text size="xs" color="dimmed" align="center">
                    No more tasks to load
                  </Text>
                </Timeline.Item>
              )}
            </Timeline>
          )}
        </Box>
      </ScrollArea>
    </Box>
  );
};
